import React from 'react'
import { FormattedMessage } from 'react-intl'

import { Button, Text } from 'components/base'
import ModalBody from 'components/Utils/Modal/Body'

import SearchBox from './SearchBox'

import useChannelsRefresher from './hooks/useChannelsRefresher'

import useMoveNotesTo from './hooks/useMoveNotesTo'
import useEntries from './hooks/useEntries'

import Entry from './Entry'
import DocTitleWithIcon from './DocTitleWithIcon'

export default function MoveToModal({
  noteIds,
  title,
  description,
  onModalClose,
}) {
  useChannelsRefresher()
  const [query, setQuery] = React.useState('')
  const inputRef = React.useRef()
  const [expandedEntryIds, setExpandedEntryIds] = React.useState(new Set())
  const [focusedEntryKey, setFocusedEntryKey] = React.useState(null)
  const [selection, setSelection] = React.useState(null) // should later be of the form {id: 'foo', key: '0-foo', onSelectData: {}}
  const toggleExpandEntry = React.useCallback(
    id => {
      if (expandedEntryIds.has(id)) {
        expandedEntryIds.delete(id)
      } else {
        expandedEntryIds.add(id)
      }
      setExpandedEntryIds(new Set(expandedEntryIds))
    },
    [expandedEntryIds]
  )

  const moveNotesTo = useMoveNotesTo(noteIds)

  const applyMoveTo = React.useCallback(
    () => {
      if (!selection) {
        return
      }
      moveNotesTo(selection.onSelectData)
      onModalClose()
    },
    [moveNotesTo, selection, onModalClose]
  )
  const focusSearchInput = React.useCallback(() => {
    inputRef.current.focus()
  }, [])

  const onSelect = React.useCallback(selection => {
    setSelection(selection)
    setFocusedEntryKey(selection.key)
  }, [])

  const [entries, navigableEntryKeys] = useEntries({
    query,
    selectedEntryId: selection ? selection.id : null,
    focusedEntryKey,
    expandedEntryIds,
    onBlur: focusSearchInput,
    onSelect,
    onToggleExpand: toggleExpandEntry,
  })

  const focusFirstRow = React.useCallback(
    () => {
      if (navigableEntryKeys.length) {
        setFocusedEntryKey(navigableEntryKeys[0])
      }
    },
    [navigableEntryKeys]
  )

  const focusLastRow = React.useCallback(
    () => {
      if (navigableEntryKeys.length) {
        setFocusedEntryKey(navigableEntryKeys[navigableEntryKeys.length - 1])
      }
    },
    [navigableEntryKeys]
  )

  const focusNextRow = React.useCallback(
    () => {
      const focusedRowIndex = navigableEntryKeys.indexOf(focusedEntryKey)
      const previousFocusedEntryKeyIndex =
        (focusedRowIndex + 1) % navigableEntryKeys.length
      const nextFocusedEntryKey =
        navigableEntryKeys[previousFocusedEntryKeyIndex]
      setFocusedEntryKey(nextFocusedEntryKey)
    },
    [navigableEntryKeys, focusedEntryKey]
  )

  const focusPreviousRow = React.useCallback(
    () => {
      const focusedRowIndex = navigableEntryKeys.indexOf(focusedEntryKey)
      const previousFocusedEntryKeyIndex =
        focusedRowIndex === 0
          ? navigableEntryKeys.length - 1
          : focusedRowIndex - 1

      const nextFocusedEntryKey =
        navigableEntryKeys[previousFocusedEntryKeyIndex]
      setFocusedEntryKey(nextFocusedEntryKey)
    },
    [navigableEntryKeys, focusedEntryKey]
  )

  return (
    <ModalBody style={{ padding: 24 }}>
      <Text.Title className="line-height-larger margin-bottom-medium">
        Move to
      </Text.Title>
      {description && (
        <div className="shade-20 margin-bottom-medium">{description}</div>
      )}
      {/* <FocusNavigator> */}

      <div className="radius-normal border-small border-shade-40">
        <SearchBox
          inputRef={inputRef}
          value={query}
          onChange={query => {
            setQuery(query)
            setFocusedEntryKey(null)
            setSelection(null)
          }}
          onFocusFirstRow={focusFirstRow}
          onFocusLastRow={focusLastRow}
        />
        <div style={{ height: 305, padding: 1 }} className="scroll-y">
          {entries.map(entryProps => {
            return (
              <Entry
                key={entryProps.key}
                entryKey={entryProps.key}
                tabIndex="0"
                {...entryProps}
                onFocusNextRow={focusNextRow}
                onFocusPreviousRow={focusPreviousRow}
              />
            )
          })}
        </div>
      </div>
      {/* </FocusNavigator> */}
      <div className="flexbox margin-top-larger font-weight-medium flexbox-space-between ellipsis">
        <Button.Secondary onClick={onModalClose} tabIndex="-1">
          Close
        </Button.Secondary>
        {selection && (
          <div className="flexbox flexbox-align-center shade-10">
            Moving{' '}
            {noteIds.length === 1 ? (
              <DocTitleWithIcon
                id={noteIds[0]}
                fallback={
                  <span className="font-weight-bold shade-05">1 item</span>
                }
              />
            ) : (
              <span className="font-weight-bold shade-05 margin-horizontal-smaller">
                <FormattedMessage
                  id="moveto.title"
                  defaultMessage="{count} {count, plural, one {doc} other {docs} }"
                  values={{
                    count: noteIds.length,
                  }}
                />
              </span>
            )}{' '}
            into{' '}
            <span
              style={{ maxWidth: 168 }}
              className="font-weight-bold ellipsis shade-05 margin-horizontal-smaller"
            >
              {selection.onSelectData.title}
            </span>
            <Button.Primary
              onClick={applyMoveTo}
              tabIndex="0"
              autoFocus
              className="margin-left-normal"
            >
              {'Move'}
            </Button.Primary>
          </div>
        )}
      </div>
    </ModalBody>
  )
}
